import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../util/Toast';
import {
    Settings as SettingsIcon,
    Search,
    Plus,
    Lock,
    Copy,
    Check,
    ChevronDown,
    X,
    Save,
    Loader2,
    Zap,
    Shield,
    Database,
    AlertCircle,
    Expand,
    Minimize2,
    Globe,
    EyeOff,
} from 'lucide-react';
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};
const AdminSettings = () => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedIds, setExpandedIds] = useState(new Set());

    // Local edit values (for staged changes)
    const [editValues, setEditValues] = useState({});
    const [editActive, setEditActive] = useState({});
    const [editStatus, setEditStatus] = useState({});
    const [savingIds, setSavingIds] = useState(new Set());
    const [copiedId, setCopiedId] = useState(null);

    // Add modal
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [modalAnimating, setModalAnimating] = useState(false);
    const [newSetting, setNewSetting] = useState({
        key: '',
        value: '',
        type: 'string',
        description: '',
        status: 'private',
    });
    const [submitting, setSubmitting] = useState(false);

    // ==================== FETCH ====================
    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const token = getCookie('adminToken') || getCookie('authToken');

            const response = await axios.get(`${baseUrl}/api/admin/settings`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (response.data?.status) {
                const list = response.data.data || [];
                setSettings(list);

                // Initialize edit state
                const values = {};
                const actives = {};
                const statuses = {};
                list.forEach((s) => {
                    values[s.id] = s.setting_value ?? '';
                    actives[s.id] = !!s.is_active;
                    statuses[s.id] = s.status || 'private';
                });
                setEditValues(values);
                setEditActive(actives);
                setEditStatus(statuses);

                // Auto-expand first setting
                if (list.length > 0) {
                    setExpandedIds(new Set([list[0].id]));
                }
            } else {
                toast.error(response.data?.msg || 'Failed to load settings');
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error(error.response?.data?.msg || 'Failed to load settings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // ==================== FILTER ====================
    const filteredSettings = useMemo(() => {
        if (!searchQuery.trim()) return settings;
        const q = searchQuery.toLowerCase();
        return settings.filter(
            (s) =>
                (s.setting_key || '').toLowerCase().includes(q) ||
                (s.description || '').toLowerCase().includes(q) ||
                (s.setting_value || '').toLowerCase().includes(q)
        );
    }, [settings, searchQuery]);

    // ==================== EXPAND / COLLAPSE ====================
    const toggleExpanded = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const expandAll = () => {
        setExpandedIds(new Set(filteredSettings.map((s) => s.id)));
    };

    const collapseAll = () => {
        setExpandedIds(new Set());
    };

    const allExpanded =
        filteredSettings.length > 0 &&
        filteredSettings.every((s) => expandedIds.has(s.id));

    // ==================== EDIT TRACKING ====================
    const isDirty = (setting) => {
        return (
            String(editValues[setting.id] ?? '') !== String(setting.setting_value ?? '') ||
            !!editActive[setting.id] !== !!setting.is_active ||
            (editStatus[setting.id] || 'private') !== (setting.status || 'private')
        );
    };

    const updateValue = (id, value) => {
        setEditValues((prev) => ({ ...prev, [id]: value }));
    };

    const toggleActive = (id) => {
        setEditActive((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const updateStatus = (id, status) => {
        setEditStatus((prev) => ({ ...prev, [id]: status }));
    };

    // ==================== SAVE ====================
    const handleSave = async (setting) => {
        setSavingIds((prev) => new Set(prev).add(setting.id));
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const token = getCookie('adminToken') || getCookie('authToken');

            const response = await axios.post(
                `${baseUrl}/api/admin/settings/update`,
                {
                    id: setting.id,
                    value: editValues[setting.id],
                    is_active: editActive[setting.id],
                    status: editStatus[setting.id] || 'private',
                },
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );

            if (response.data?.status) {
                toast.success(response.data.msg || 'Setting updated');
                // Update local source of truth
                setSettings((prev) =>
                    prev.map((s) =>
                        s.id === setting.id
                            ? {
                                ...s,
                                setting_value: editValues[setting.id],
                                is_active: editActive[setting.id],
                                status: editStatus[setting.id] || 'private',
                                updated_at: new Date().toLocaleString(),
                            }
                            : s
                    )
                );
            } else {
                toast.error(response.data?.msg || 'Failed to update');
            }
        } catch (error) {
            console.error('Error saving setting:', error);
            toast.error(error.response?.data?.msg || 'Failed to update');
        } finally {
            setSavingIds((prev) => {
                const next = new Set(prev);
                next.delete(setting.id);
                return next;
            });
        }
    };

    // ==================== ADD ====================
    const openAddModal = () => {
        setNewSetting({ key: '', value: '', type: 'string', description: '', status: 'private' });
        setIsAddModalOpen(true);
        requestAnimationFrame(() => setModalAnimating(true));
    };

    const closeAddModal = () => {
        setModalAnimating(false);
        setTimeout(() => setIsAddModalOpen(false), 300);
    };

    const handleAddSetting = async (e) => {
        e.preventDefault();
        if (!newSetting.key.trim()) {
            toast.error('Setting key is required');
            return;
        }
        setSubmitting(true);
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const token = getCookie('adminToken') || getCookie('authToken');

            const response = await axios.post(
                `${baseUrl}/api/admin/settings/add`,
                {
                    key: newSetting.key.trim(),
                    value: newSetting.value,
                    type: newSetting.type,
                    description: newSetting.description,
                    status: newSetting.status,
                },
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );

            if (response.data?.status) {
                toast.success(response.data.msg || 'Setting created');
                closeAddModal();
                fetchSettings();
            } else {
                toast.error(response.data?.msg || 'Failed to create setting');
            }
        } catch (error) {
            console.error('Error adding setting:', error);
            toast.error(error.response?.data?.msg || 'Failed to create setting');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== COPY ====================
    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    // ==================== HELPERS ====================
    const getTypeMeta = (type) => {
        const t = String(type || '').toLowerCase();
        if (t === 'number') return { icon: <Database className="w-3 h-3" />, color: 'indigo' };
        if (t === 'boolean') return { icon: <Zap className="w-3 h-3" />, color: 'amber' };
        if (t === 'json') return { icon: <Shield className="w-3 h-3" />, color: 'rose' };
        return { icon: <SettingsIcon className="w-3 h-3" />, color: 'emerald' };
    };

    const typeColorClasses = (color) => {
        switch (color) {
            case 'indigo':
                return 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20';
            case 'amber':
                return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
            case 'rose':
                return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
            default:
                return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
        }
    };

    // Custom status segmented control (reused in edit + add)
    const StatusSelector = ({ value, onChange, size = 'md' }) => {
        const isPublic = value === 'public';
        const pad = size === 'sm' ? 'py-2' : 'py-2.5';
        const textSize = size === 'sm' ? 'text-[10px]' : 'text-xs';
        return (
            <div className="flex bg-zinc-950 border border-zinc-800 rounded-2xl p-1 gap-1">
                <button
                    type="button"
                    onClick={() => onChange('public')}
                    className={`flex-1 flex items-center justify-center gap-1.5 ${pad} rounded-xl ${textSize} font-black uppercase tracking-widest transition-all ${isPublic
                        ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                        : 'text-zinc-400 hover:text-zinc-100'
                        }`}
                >
                    <Globe className="w-3.5 h-3.5" />
                    Public
                </button>
                <button
                    type="button"
                    onClick={() => onChange('private')}
                    className={`flex-1 flex items-center justify-center gap-1.5 ${pad} rounded-xl ${textSize} font-black uppercase tracking-widest transition-all ${!isPublic
                        ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/20'
                        : 'text-zinc-400 hover:text-zinc-100'
                        }`}
                >
                    <EyeOff className="w-3.5 h-3.5" />
                    Private
                </button>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
            <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-spring-in { animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        @keyframes expandDown {
          0% { opacity: 0; max-height: 0; }
          100% { opacity: 1; max-height: 700px; }
        }
        .animate-expand { animation: expandDown 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards; overflow: hidden; }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }
        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }

        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 8px; }
      `}</style>

            {/* ==================== HEADER (non-sticky) ==================== */}
            <div className="px-4 sm:px-8 pt-4 sm:pt-8 pb-4">
                <div className="max-w-[900px] mx-auto space-y-4">

                    {/* Row 1: Title */}
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-emerald-500 rounded-2xl text-zinc-950 shadow-lg shadow-emerald-500/30 shrink-0">
                            <SettingsIcon className="w-5 h-5 animate-[spin_4s_linear_infinite]" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-xl sm:text-2xl font-black tracking-tight text-zinc-100 leading-tight">
                                System Core
                            </h2>
                            <p className="text-zinc-500 text-xs font-medium mt-0.5">
                                Manage global variables and platform behavior
                            </p>
                        </div>
                    </div>

                    {/* Row 2: Search + Actions */}
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1 min-w-0 group">
                            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within:text-emerald-400 transition-colors" />
                            <input
                                placeholder="Search settings..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-sm font-bold outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 transition-all text-zinc-100 placeholder:text-zinc-600"
                            />
                        </div>

                        <button
                            onClick={allExpanded ? collapseAll : expandAll}
                            title={allExpanded ? 'Collapse All' : 'Expand All'}
                            className="p-2.5 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 rounded-xl transition-all micro-interaction shrink-0"
                        >
                            {allExpanded ? (
                                <Minimize2 className="w-4 h-4" />
                            ) : (
                                <Expand className="w-4 h-4" />
                            )}
                        </button>

                        <button
                            onClick={openAddModal}
                            className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-1.5 shadow-lg shadow-emerald-500/20 smooth-transition micro-interaction shrink-0"
                        >
                            <Plus className="w-4 h-4" />
                            <span className="hidden sm:inline">Add New</span>
                        </button>
                    </div>

                </div>
            </div>

            {/* ==================== SETTINGS LIST ==================== */}
            <div className="p-4 sm:p-8 max-w-[1200px] mx-auto space-y-3 pb-20 -mt-4 sm:-mt-8">

                {loading ? (
                    [...Array(4)].map((_, i) => (
                        <div
                            key={i}
                            className="bg-zinc-900/90 border border-zinc-800 rounded-[2rem] p-6 shimmer-bg h-20"
                        />
                    ))
                ) : filteredSettings.length === 0 ? (
                    <div className="bg-zinc-900/90 border border-zinc-800 rounded-[2.5rem] py-20 text-center animate-spring-in">
                        <AlertCircle className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                        <p className="text-sm font-bold text-zinc-500 italic">
                            {searchQuery
                                ? 'No settings match your search.'
                                : 'No settings found.'}
                        </p>
                    </div>
                ) : (
                    filteredSettings.map((setting, idx) => {
                        const expanded = expandedIds.has(setting.id);
                        const dirty = isDirty(setting);
                        const saving = savingIds.has(setting.id);
                        const typeMeta = getTypeMeta(setting.type);
                        const typeColor = typeColorClasses(typeMeta.color);
                        const isBool = String(setting.type).toLowerCase() === 'boolean';
                        const boolValue = String(editValues[setting.id]).toLowerCase() === 'true';
                        const currentStatus = editStatus[setting.id] || 'private';

                        return (
                            <div
                                key={setting.id}
                                style={{ animationDelay: `${idx * 30}ms` }}
                                className={`bg-zinc-900/90 border rounded-[2rem] shadow-lg overflow-hidden transition-all duration-300 animate-spring-in ${dirty
                                    ? 'border-emerald-500/40 shadow-emerald-500/10'
                                    : 'border-zinc-800'
                                    }`}
                            >
                                {/* ==================== COLLAPSED HEADER ==================== */}
                                <button
                                    onClick={() => toggleExpanded(setting.id)}
                                    className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 hover:bg-zinc-950/40 transition-colors text-left"
                                >
                                    <div className="flex items-center gap-4 min-w-0 flex-1">
                                        {/* Active Dot */}
                                        <div className="shrink-0">
                                            <div
                                                className={`w-2.5 h-2.5 rounded-full transition-all ${editActive[setting.id]
                                                    ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.6)]'
                                                    : 'bg-zinc-700'
                                                    }`}
                                            />
                                        </div>

                                        {/* Type Badge */}
                                        <div
                                            className={`shrink-0 px-2.5 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest flex items-center gap-1 ${typeColor}`}
                                        >
                                            {typeMeta.icon}
                                            <span className="hidden sm:inline">
                                                {setting.type || 'string'}
                                            </span>
                                        </div>

                                        {/* Status Badge (custom, matches design) */}
                                        <div
                                            className={`hidden sm:flex shrink-0 px-2.5 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest items-center gap-1 ${currentStatus === 'public'
                                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                                : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                                                }`}
                                        >
                                            {currentStatus === 'public' ? (
                                                <Globe className="w-3 h-3" />
                                            ) : (
                                                <EyeOff className="w-3 h-3" />
                                            )}
                                            <span>{currentStatus}</span>
                                        </div>

                                        {/* Name + Key */}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="text-sm sm:text-base font-black text-zinc-100 capitalize leading-tight truncate">
                                                    {(setting.setting_key || '').replace(/_/g, ' ')}
                                                </h3>
                                                {dirty && (
                                                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-md text-[9px] font-black uppercase">
                                                        Modified
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[10px] font-mono font-bold text-zinc-500 mt-0.5 truncate">
                                                {setting.setting_key}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Right side: value preview + chevron */}
                                    <div className="flex items-center gap-3 shrink-0">
                                        <div className="hidden md:block max-w-[200px]">
                                            <p className="text-[10px] font-mono text-zinc-500 truncate text-right">
                                                {String(editValues[setting.id] ?? '').slice(0, 40)}
                                                {String(editValues[setting.id] ?? '').length > 40 ? '…' : ''}
                                            </p>
                                        </div>
                                        <div
                                            className={`p-2 rounded-xl transition-all ${expanded
                                                ? 'bg-emerald-500/10 text-emerald-400 rotate-180'
                                                : 'text-zinc-500'
                                                }`}
                                        >
                                            <ChevronDown className="w-4 h-4" />
                                        </div>
                                    </div>
                                </button>

                                {/* ==================== EXPANDED CONTENT ==================== */}
                                {expanded && (
                                    <div className="border-t border-zinc-800/60 animate-expand">
                                        <div className="p-5 sm:p-6 space-y-4">

                                            {/* Description */}
                                            {setting.description && (
                                                <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                                                    {setting.description}
                                                </p>
                                            )}

                                            <div className="flex flex-col lg:flex-row gap-5">
                                                {/* Left: Active toggle + status + metadata */}
                                                <div className="lg:w-1/3 space-y-4">
                                                    <div className="flex items-center gap-3">
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                className="sr-only peer"
                                                                checked={!!editActive[setting.id]}
                                                                onChange={() => toggleActive(setting.id)}
                                                            />
                                                            <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                                                        </label>
                                                        <span
                                                            className={`text-[10px] font-black uppercase tracking-widest ${editActive[setting.id]
                                                                ? 'text-emerald-400'
                                                                : 'text-zinc-500'
                                                                }`}
                                                        >
                                                            {editActive[setting.id] ? 'Enabled' : 'Disabled'}
                                                        </span>
                                                    </div>

                                                    {/* Status selector (custom) */}
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                                                            <Globe className="w-3 h-3" />
                                                            <span>Visibility</span>
                                                        </div>
                                                        <StatusSelector
                                                            value={currentStatus}
                                                            onChange={(v) => updateStatus(setting.id, v)}
                                                            size="sm"
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                                                            <Lock className="w-3 h-3" />
                                                            <span>Last Modified</span>
                                                        </div>
                                                        <p className="text-[10px] font-mono text-zinc-400">
                                                            {setting.updated_at || '—'}
                                                        </p>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2.5 py-1 bg-zinc-950 border border-zinc-800 rounded-lg text-[9px] font-black uppercase tracking-widest text-emerald-400">
                                                            Core Property
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Right: Value input + Save */}
                                                <div className="flex-1 space-y-3">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                                                        Configuration Value
                                                    </label>

                                                    {isBool ? (
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex-1 flex bg-zinc-950 border border-zinc-800 rounded-2xl p-1">
                                                                <button
                                                                    onClick={() => updateValue(setting.id, 'true')}
                                                                    className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${boolValue
                                                                        ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                                                                        : 'text-zinc-400 hover:text-zinc-100'
                                                                        }`}
                                                                >
                                                                    True
                                                                </button>
                                                                <button
                                                                    onClick={() => updateValue(setting.id, 'false')}
                                                                    className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${!boolValue
                                                                        ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/20'
                                                                        : 'text-zinc-400 hover:text-zinc-100'
                                                                        }`}
                                                                >
                                                                    False
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="relative">
                                                            <input
                                                                type={String(setting.type).toLowerCase() === 'number' ? 'number' : 'text'}
                                                                value={editValues[setting.id] ?? ''}
                                                                onChange={(e) => updateValue(setting.id, e.target.value)}
                                                                className="w-full pl-5 pr-14 py-4 bg-zinc-950 border border-zinc-800 rounded-2xl text-sm font-bold text-zinc-100 outline-none focus:ring-2 focus:ring-emerald-500/15 focus:border-emerald-500/60 transition-all"
                                                            />
                                                            <button
                                                                onClick={() =>
                                                                    handleCopy(String(editValues[setting.id] ?? ''), setting.id)
                                                                }
                                                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-zinc-500 hover:text-emerald-400 transition-colors micro-interaction"
                                                                title="Copy value"
                                                            >
                                                                {copiedId === setting.id ? (
                                                                    <Check className="w-4 h-4 text-emerald-400" />
                                                                ) : (
                                                                    <Copy className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}

                                                    {dirty && (
                                                        <button
                                                            onClick={() => handleSave(setting)}
                                                            disabled={saving}
                                                            className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 smooth-transition micro-interaction disabled:opacity-50"
                                                        >
                                                            {saving ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Save className="w-4 h-4" />
                                                            )}
                                                            {saving ? 'Saving...' : 'Save Changes'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* ==================== ADD NEW MODAL ==================== */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
                    <div
                        onClick={closeAddModal}
                        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${modalAnimating ? 'opacity-100' : 'opacity-0'
                            }`}
                    />
                    <div
                        className={`relative w-full max-w-lg bg-zinc-900 rounded-[2.5rem] border border-zinc-800 shadow-2xl overflow-hidden transition-all duration-300 ${modalAnimating ? 'animate-spring-in opacity-100' : 'opacity-0 scale-95'
                            }`}
                    >
                        <div className="p-6 sm:p-8 border-b border-zinc-800 flex justify-between items-center bg-zinc-950/50">
                            <div>
                                <h2 className="text-xl sm:text-2xl font-black text-zinc-100 tracking-tight">
                                    Add New Setting
                                </h2>
                                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mt-0.5">
                                    Create global config variable
                                </p>
                            </div>
                            <button
                                onClick={closeAddModal}
                                className="p-2 bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-rose-400 rounded-xl smooth-transition micro-interaction"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleAddSetting} className="p-6 sm:p-8 space-y-5">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                                    Setting Key *
                                </label>
                                <input
                                    required
                                    type="text"
                                    placeholder="e.g. NEW_FEATURE_FLAG"
                                    value={newSetting.key}
                                    onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value })}
                                    className="w-full px-4 py-3.5 bg-zinc-950 border border-zinc-800 rounded-2xl font-bold text-sm outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 transition-all text-zinc-100 placeholder:text-zinc-600"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                                    Type
                                </label>
                                <div className="grid grid-cols-4 gap-2">
                                    {['string', 'number', 'boolean', 'json'].map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => setNewSetting({ ...newSetting, type: t })}
                                            className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${newSetting.type === t
                                                ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                                                : 'bg-zinc-950 text-zinc-400 border border-zinc-800 hover:border-zinc-700'
                                                }`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                                    Value
                                </label>
                                <input
                                    type="text"
                                    placeholder="Initial value"
                                    value={newSetting.value}
                                    onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
                                    className="w-full px-4 py-3.5 bg-zinc-950 border border-zinc-800 rounded-2xl font-bold text-sm outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 transition-all text-zinc-100 placeholder:text-zinc-600"
                                />
                            </div>

                            {/* Status selector (custom) */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                                    Visibility
                                </label>
                                <StatusSelector
                                    value={newSetting.status}
                                    onChange={(v) => setNewSetting({ ...newSetting, status: v })}
                                />
                                <p className="text-[10px] text-zinc-600 font-medium ml-1 leading-relaxed">
                                    Public settings are exposed to clients. Private settings stay admin-only.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                                    Description
                                </label>
                                <textarea
                                    rows={2}
                                    placeholder="Brief description of what this does..."
                                    value={newSetting.description}
                                    onChange={(e) =>
                                        setNewSetting({ ...newSetting, description: e.target.value })
                                    }
                                    className="w-full px-4 py-3.5 bg-zinc-950 border border-zinc-800 rounded-2xl font-medium text-sm outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 transition-all text-zinc-100 placeholder:text-zinc-600 resize-none"
                                />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeAddModal}
                                    className="flex-1 py-3 bg-zinc-950 border border-zinc-800 text-zinc-300 rounded-xl font-black text-xs uppercase tracking-wider hover:border-zinc-700 smooth-transition micro-interaction"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl font-black text-xs uppercase tracking-wider shadow-lg shadow-emerald-500/20 smooth-transition micro-interaction disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {submitting ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <Plus className="w-3.5 h-3.5" />
                                    )}
                                    {submitting ? 'Creating...' : 'Create Setting'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default AdminSettings;